// Package imports:
import React, { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts/highstock';
import ISLocale from 'date-fns/locale/is';
import { format } from 'date-fns';
// Component imports:
import DisplayBox from '../../DisplayBox/DisplayBox';
// Service imports:
import { formatNumber, getAPItoday } from '../../../services/utils';
import { useApiLmdData } from '../../../services/apiHooks';
import { SHORT_MONTHS } from '../../../services/config';
// Type imports:
import { IDefaultProps } from '../../../types/Types';
import { IApiLmdIndexesChart } from '../../../types/MarketTypes';

type ChartType = 'index' | 'currency' | 'indices'; // Will add more types later...

interface IChartDatum {
    x: number | undefined
    y: number | undefined
};
interface IChartDataInfo {
    name: string,
    data: IChartDatum[]
};

interface IOwnProps {
    chartType?: ChartType,
    symbol?: string,
    title?: string,
    height?: number
};

type Props = IOwnProps & IDefaultProps & React.HTMLAttributes<HTMLDivElement>;

const IndicesChart: React.FC<Props> = ({
    chartType,
    title,
    height = 500,
    accessToken,
    refreshRateMs,
    ...props
}) => {
    const today = new Date();
    const from = new Date(today.getFullYear() - 5, today.getMonth() + 1, today.getDate());
    const [dataOMXI10] = useApiLmdData<IApiLmdIndexesChart[]>(
        `/v1/market_data/v1/indexes/symbol/OMXI10/snapshot/?from_date=${getAPItoday(from)}&to_date=${getAPItoday(today)}`,
        accessToken,
        refreshRateMs
    );
    const [dataOMXN40] = useApiLmdData<IApiLmdIndexesChart[]>(
        `/v1/market_data/v1/indexes/symbol/OMXN40/snapshot/?from_date=${getAPItoday(from)}&to_date=${getAPItoday(today)}`,
        accessToken,
        refreshRateMs
    );
    const [dataNOMXIBB] = useApiLmdData<IApiLmdIndexesChart[]>(
        `/v1/market_data/v1/indexes/symbol/NOMXIBB/snapshot/?from_date=${getAPItoday(from)}&to_date=${getAPItoday(today)}`,
        accessToken,
        refreshRateMs
    );
    const [ selectedRange, setSelectedRange ] = useState(6);

    const divRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        // OMXI10
        const chartDataInfoOMXI10: IChartDataInfo = {
            name: 'OMX Iceland 10',
            data: []
        }
        if (dataOMXI10.data !== null && !(dataOMXI10.error instanceof Error)) {
            for (let datum of dataOMXI10.data) {
                chartDataInfoOMXI10.data.push({
                    x: datum.trade_date ? Date.parse(datum.trade_date.replace(/ /g, "T")) : undefined,
                    y: datum.current_value ?? undefined
                });
            }
        }

        //OMXN40
        const chartDataInfoOMXN40: IChartDataInfo = {
            name: 'OMX Nordic 40',
            data: []
        }
        if (dataOMXN40.data !== null && !(dataOMXN40.error instanceof Error)) {
            for (let datum of dataOMXN40.data) {
                chartDataInfoOMXN40.data.push({
                    x: datum.trade_date ? Date.parse(datum.trade_date.replace(/ /g, "T")) : undefined,
                    y: datum.current_value ?? undefined
                });
            }
        }

        //NOMXIBB
        const chartDataInfoNOMXIBB: IChartDataInfo = {
            name: 'NASDQ OMX Iceland Benchmark Bonds',
            data: []
        }
        if (dataNOMXIBB.data !== null && !(dataNOMXIBB.error instanceof Error)) {
            for (let datum of dataNOMXIBB.data) {
                chartDataInfoNOMXIBB.data.push({
                    x: datum.trade_date ? Date.parse(datum.trade_date.replace(/ /g, "T")) : undefined,
                    y: datum.current_value ?? undefined
                });
            }
        }

        Highcharts.setOptions({
            lang: {
                shortMonths: SHORT_MONTHS,
                resetZoom: 'Til baka',
                resetZoomTitle: ''
            }
        });

        if (divRef.current !== null) {
            Highcharts.stockChart(divRef.current, {
                chart: {
                    height: height,
                    marginTop: 80,
                    reflow: true,
                    style: {
                        fontFamily: "Roboto"
                    },
                    zoomType: "xy",
                    selectionMarkerFill: '#4569EE18',
                    resetZoomButton: {
                        position: {
                            x: 0,
                            y: -75
                        },
                        theme: {
                            r: 6,
                            height: 28
                        }
                    },
                    plotBackgroundColor: {
                        linearGradient: {x1: 0, x2: 0, y1: 0, y2: 1},
                        stops: [
                            [0.05, '#FFF'],
                            [0.95, '#F8F8FA']
                        ]
                    }
                },
                exporting: {
                    buttons: {
                        contextButton: {
                            x: 30,
                            y: 36
                        }
                    }
                },
                credits: {
                    enabled: false
                },
                title: {
                    text: '',
                    align: 'center',
                    x: 60,
                    margin: -14
                },
                navigator: {
                    enabled: true,
                    adaptToUpdatedData: false,
                    handles: {
                        height: 22,
                        width: 6
                    },
                    maskFill: '#D4DEFF80',
                    series: {
                        lineWidth: 0,
                        opacity: 0
                    },
                    height: 20,
                    xAxis: {
                        labels: {
                            style: {
                                color: '#7B7C83',
                                fontSize: '10px'
                            },
                            y: -6
                        },
                        gridLineWidth: 0,
                        minTickInterval: 1000*60*60*24*365,
                        lineWidth: 0
                    },
                    yAxis: {
                        lineWidth: 0
                    },
                    outlineWidth: 0
                },
                scrollbar: {
                    enabled: false
                },
                tooltip: {
                    enabled: true,
                    formatter: function() {
                        //filtera einungis það sem er visible af því ef það er notað points[0] crashar allt þegar eitthvað series er disable-að
                        return (`<div class="chart-tooltip">
                                    <p class="tooltip-date">${format(this.x, 'EEEE, dd. MMM yyyy', {locale: ISLocale})}</p>
                                    ${this.points?.filter(x => x.point.visible === true).map(x => (
                                        `<p><span style="color: ${x.color}">${x.series.getName()}</span>: ${formatNumber(x.y)}</p>`
                                    ))}
                                </div>`).replace(/>,</g,'><')
                        
                    },
                    borderWidth: 0,
                    shadow: false,
                    useHTML: true
                },
                xAxis: [{
                    type: 'datetime',
                    title: {
                        text: null
                    },
                    minTickInterval: 1000*60*60*24*30,
                    tickWidth: 0
                }],
                yAxis: {
                    title: {
                        text: 'Gildi',
                        offset: 52,
                        x: 5
                    },
                    gridLineDashStyle: 'Dash',
                    gridLineWidth: 1,
                    gridLineColor: '#D3D3D6',
                    opposite: false,
                    labels: {
                        x: -11,
                        y: 2
                    }
                },
                plotOptions: {
                    series: {
                        color: '#4569EE',
                        enableMouseTracking: true,
                        marker: {
                            enabled: false
                        },
                        animation: false,
                        turboThreshold: 2000
                    }
                },
                legend: {
                    enabled: true,
                    align: 'left',
                    verticalAlign: 'top',
                    itemStyle: {
                        color: '#232530',
                        fontSize: '14px',
                        fontWeight: '400',
                        lineHeight: '26px'
                    },
                    x: 5,
                    y: 10
                },
                rangeSelector: {
                    allButtonsEnabled: true,
                    buttonPosition: {
                        align: 'right',
                        x: 0,
                        y: -77
                    },
                    buttons: [{
                        type: 'month',
                        count: 1,
                        text: '1 mán.',
                        title: '1 mánuður',
                        events: {
                            click: () => setSelectedRange(0)
                        }
                    }, {
                        type: 'month',
                        count: 3,
                        text: '3 mán.',
                        title: '3 mánuðir',
                        events: {
                            click: () => setSelectedRange(1)
                        }
                    }, {
                        type: 'month',
                        count: 6,
                        text: '6 mán.',
                        title: '6 mánuðir',
                        events: {
                            click: () => setSelectedRange(2)
                        }
                    }, {
                        type: 'ytd',
                        text: 'Þetta ár',
                        title: 'Þetta ár',
                        events: {
                            click: () => setSelectedRange(3)
                        }
                    }, {
                        type: 'year',
                        count: 1,
                        text: '1 ár',
                        title: '1 ár',
                        events: {
                            click: () => setSelectedRange(4)
                        }
                    }, {
                        type: 'year',
                        count: 3,
                        text: '3 ár',
                        title: '3 ár',
                        events: {
                            click: () => setSelectedRange(5)
                        }
                    }, {
                        type: 'year',
                        count: 5,
                        text: '5 ár',
                        title: '5 ár',
                        events: {
                            click: () => setSelectedRange(6)
                        }
                    }],
                    buttonSpacing: 5,
                    buttonTheme: {
                        r: 6,
                        height: 25,
                        width: 50
                    },
                    inputEnabled: false,
                    selected: selectedRange,
                    labelStyle: {
                        display: 'none'
                    },
                    dropdown: 'never'
                },
                responsive: {
                    rules: [{
                        condition: {
                            maxWidth: 369
                        },
                        chartOptions: {
                            rangeSelector: {
                                dropdown: 'always'
                            }
                        }
                    }, {
                        condition: {
                           callback: function() {
                               return window.matchMedia('(max-width: 767px)').matches;
                           }
                        },
                        chartOptions: {
                            chart: {
                                spacingLeft: 0,
                                spacingRight: 5,
                                marginTop: 60
                            },
                            legend: {
                                x: 10,
                                y: -20,
                                itemStyle: {
                                    fontSize: '12px'
                                }
                            },
                            navigator: {
                                margin: 13
                            },
                            rangeSelector: {
                                buttonPosition: {
                                    y: -60
                                },
                                buttonTheme: {
                                    width: 36,
                                    height: 24
                                }
                            },
                            xAxis: {
                                labels: {
                                    style: {
                                        fontSize: '9px'
                                    }
                                }
                            },
                            yAxis: {
                                labels: {
                                    style: {
                                        fontSize: '9px'
                                    },
                                    x: -1
                                },
                                title: {
                                    offset: 27,
                                    x: 0,
                                    style: {
                                        fontSize: '10px'
                                    }
                                }
                            }
                        }
                    }]
                },
                series: [{
                    type:'line',
                    name: 'OMX Iceland 10',
                    color: '#4569EE',
                    data: chartDataInfoOMXI10.data
                }, {
                    type: 'line',
                    name: 'OMX Nordic 40',
                    color: '#FF7697',
                    data: chartDataInfoOMXN40.data
                }, {
                    type: 'line',
                    name: 'NASDQ OMX Iceland Benchmark Bonds',
                    color: '#48A85D',
                    data: chartDataInfoNOMXIBB.data,
                }]
            }, () => {});
        }
    }, [ dataOMXI10, dataOMXN40, dataNOMXIBB, divRef.current]);

    return (
        <DisplayBox 
            className='KCL_chart'
            title={title}
            {...props}
        >
            <div className="chart" ref={divRef}></div>
        </DisplayBox>
    );
}

export default IndicesChart;